<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-cached</v-icon>
            Order Cache Management
          </v-card-title>

          <v-card-subtitle>
            Refresh order cache from Firebase for specific weeks
          </v-card-subtitle>

          <v-card-text>
            <v-alert type="info" dense>
              Use these buttons to update the order cache from Firebase for the current week and upcoming weeks.
            </v-alert>

            <v-row class="mt-4">
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-title class="text-h6">Current Week</v-card-title>
                  <v-card-text>
                    <div class="text-body-1 mb-3">
                      {{ currentWeekDate }}
                    </div>
                    <v-btn
                      color="primary"
                      :loading="loading.currentWeek"
                      :disabled="isAnyLoading"
                      @click="refreshCache(currentWeekDate, 'currentWeek')"
                      block
                      large
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Refresh Current Week
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-title class="text-h6">Next Week</v-card-title>
                  <v-card-text>
                    <div class="text-body-1 mb-3">
                      {{ nextWeekDate }}
                    </div>
                    <v-btn
                      color="primary"
                      :loading="loading.nextWeek"
                      :disabled="isAnyLoading"
                      @click="refreshCache(nextWeekDate, 'nextWeek')"
                      block
                      large
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Refresh Next Week
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-title class="text-h6">Week After</v-card-title>
                  <v-card-text>
                    <div class="text-body-1 mb-3">
                      {{ weekAfterDate }}
                    </div>
                    <v-btn
                      color="primary"
                      :loading="loading.weekAfter"
                      :disabled="isAnyLoading"
                      @click="refreshCache(weekAfterDate, 'weekAfter')"
                      block
                      large
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Refresh Week After
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Results Section -->
    <v-row v-if="results.length > 0" class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-history</v-icon>
            Recent Operations
          </v-card-title>

          <v-card-text>
            <v-timeline dense>
              <v-timeline-item
                v-for="(result, index) in results"
                :key="index"
                :color="result.success ? 'success' : 'error'"
                small
              >
                <template v-slot:icon>
                  <v-icon small color="white">
                    {{ result.success ? 'mdi-check' : 'mdi-alert' }}
                  </v-icon>
                </template>
                <v-card>
                  <v-card-title class="text-subtitle-1">
                    {{ result.date }}
                  </v-card-title>
                  <v-card-text>
                    <div v-if="result.success">
                      <v-icon small color="success">mdi-check-circle</v-icon>
                      {{ result.message }}
                      <div class="text-caption mt-1">
                        Line Items: {{ result.lineItemCount }}
                      </div>
                    </div>
                    <div v-else>
                      <v-icon small color="error">mdi-alert-circle</v-icon>
                      Error: {{ result.error }}
                    </div>
                    <div class="text-caption mt-1">
                      {{ result.timestamp }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Error Display -->
    <v-row v-if="error">
      <v-col cols="12">
        <v-alert type="error" dismissible @input="error = null">
          <div class="text-h6">Error</div>
          <div>{{ error }}</div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';

export default {
  name: 'CacheClear',
  data() {
    return {
      loading: {
        currentWeek: false,
        nextWeek: false,
        weekAfter: false
      },
      results: [],
      error: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    };
  },
  computed: {
    currentWeekDate() {
      return moment().startOf('week').format('YYYY-MM-DD');
    },
    nextWeekDate() {
      return moment().startOf('week').add(1, 'week').format('YYYY-MM-DD');
    },
    weekAfterDate() {
      return moment().startOf('week').add(2, 'weeks').format('YYYY-MM-DD');
    },
    isAnyLoading() {
      return this.loading.currentWeek || this.loading.nextWeek || this.loading.weekAfter;
    }
  },
  methods: {
    async refreshCache(date, loadingKey) {
      this.loading[loadingKey] = true;
      this.error = null;

      try {
        const response = await api.post(`v2/order/refresh-cache/${date}`);

        const result = {
          date,
          success: true,
          message: response.data.message,
          lineItemCount: response.data.lineItemCount,
          timestamp: moment().format('YYYY-MM-DD HH:mm:ss')
        };

        this.results.unshift(result);

        this.snackbar = {
          show: true,
          message: `Successfully refreshed cache for ${date}`,
          color: 'success'
        };
      } catch (err) {
        console.error('Error refreshing cache:', err);
        const errorMessage = err.response?.data?.error || err.message || 'An error occurred while refreshing cache';

        const result = {
          date,
          success: false,
          error: errorMessage,
          timestamp: moment().format('YYYY-MM-DD HH:mm:ss')
        };

        this.results.unshift(result);
        this.error = errorMessage;

        this.snackbar = {
          show: true,
          message: `Failed to refresh cache for ${date}`,
          color: 'error'
        };
      } finally {
        this.loading[loadingKey] = false;
      }
    }
  }
};
</script>

<style scoped>
.text-h6 {
  font-weight: bold;
}

.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
</style>